import { useTollingPointSearchListQuery } from '@kinetic-ui/data-layer';
import {
  AppSearchIconKey,
  AppSearchItemType,
  DEFAULT_ZOOM,
  INTEGRATIONS_PAGE_ROUTE,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useGetNodes,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
  useRouteBuilder,
} from '@kinetic-ui/shared';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE } from '../EntityList/const';
import tollBlack from '../markers/assets/tollBlack.svg';
import { useTranslation } from 'react-i18next';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { searchText } = useMapBoxSearchContext();
  const { t } = useTranslation('tolling');
  const { push } = useHistory();
  const map = useKineticMap();
  const { addSearchHistory } = useAppSearchHistory();
  const integrationRouter = useRouteBuilder(INTEGRATIONS_PAGE_ROUTE);

  const { data } = useTollingPointSearchListQuery({
    variables: { searchText, limit: 5 },
    fetchPolicy: 'cache-and-network',
  });

  const tollingPoints = useGetNodes(data?.tollingPoints);

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = integrationRouter(id);
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const gate = tollingPoints.find((e) => e.id === id);
        if (gate?.name) {
          addSearchHistory({
            name: gate.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.TollingPoint,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      !selectedLayers.includes('tolling') && setSelectedLayers([...selectedLayers, 'tolling']);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      isAppSearch,
      hasEntityDrawer,
      selectedLayers,
      setSelectedLayers,
      push,
      map,
      integrationRouter,
      tollingPoints,
      onResultClick,
      addSearchHistory,
      addMapPin,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {tollingPoints.map(({ id, name, location }) => (
        <MapSearchResultItem
          icon={<img src={tollBlack} alt={t('tollingPoints')} />}
          onClick={(openInNewWindow) => onClick(id, location, openInNewWindow)}
          key={`searchItem_${id}`}
          name={name}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
